import React, { useEffect, useState } from 'react'
import { withTheme } from 'theming'
import { isEmpty } from 'lodash'
import CustomImage from '../../customImage/customImage'
import useTemplateHook from '../templateHook'
import AddSection from '../addSection'
import EditMenu from '../editMenu'
import PassimPlayer from '../rosemary/passimPlayer'

const INTERVAL = 7000
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'

const arrowBtn =
  'w-10 h-10 rounded-md bg-white/[0.12] text-lg text-white hover:bg-white/25 border-none'

const AthleteOwnedSportsBanner = ({
  componentData,
  modifyData,
  setModalState,
  isEdit,
  onSwap,
  onDelete,
  onCopy,
  theme,
  onAdd,
}) => {
  const { data, id } = componentData
  const { customNavigate, getTemplateImage } = useTemplateHook()
  const slides = data?.slide_data || []

  const [hoverState, setHoverState] = useState('')
  const [cur, setCur] = useState(0)

  const go = (i) => setCur((i + slides.length) % slides.length)

  // Slide count can shrink while editing
  useEffect(() => {
    if (cur >= slides.length) setCur(0)
  }, [slides.length])

  // Re-armed on every slide change, so manual navigation also restarts the countdown
  useEffect(() => {
    if (!data?.auto_slide || slides.length < 2) return
    const t = setTimeout(() => setCur((c) => (c + 1) % slides.length), INTERVAL)
    return () => clearTimeout(t)
  }, [cur, data?.auto_slide, slides.length])

  const handleHoverItems = (name) => {
    if (!isEdit) return
    setHoverState(name)
  }

  const handleModal = () => {
    setModalState({
      keyID: id,
      modalData: data,
    })
  }

  const handleAddModal = () => {
    onAdd({
      keyID: id,
      modalData: data,
    })
  }

  const handleRedirect = (linkObj) => {
    customNavigate('INDIRECT', linkObj, isEdit)
  }

  const getStyleValue = (parentKey, childKey, defaultValue) => {
    if (!data?.style_config) {
      return defaultValue ? defaultValue : ''
    }

    let tempArr = data?.style_config[parentKey]?.data.filter((info) => info.key === childKey)

    return !isEmpty(tempArr) ? (tempArr[0].value ? tempArr[0].value : defaultValue) : defaultValue
  }

  const bgColor = getStyleValue('component_background', 'primary_color', '#121318')
  const liveColor = getStyleValue('live_event', 'color', '#ffffff')
  const liveDotColor = getStyleValue('live_event', 'dot_color', '#E4022B')

  const sep = <span style={{ color: liveColor, opacity: 0.6 }}>·</span>

  return (
    <div
      style={{ background: bgColor }}
      className={`w-full relative ${isEdit && hoverState && 'component-hover-class'}`}
      onMouseOver={() => handleHoverItems(`TOP`)}
      onMouseLeave={() => handleHoverItems('')}
    >
      {isEdit && hoverState.length > 0 && (
        <EditMenu
          onDelete={() => onDelete(id)}
          onMoveUp={() => onSwap(id, 'up')}
          onMoveDown={() => onSwap(id, 'down')}
          onEdit={() => handleModal()}
          onCopy={() => onCopy(id)}
          position={'top-20 right-4'}
          menuPosition={'top-24 right-4'}
        />
      )}

      <section
        className="relative h-[34.125rem] overflow-hidden max-md:h-auto max-md:grid"
        aria-roledescription="carousel"
      >
        {slides.map((slide, idx) => {
          const active = idx === cur
          const liveTexts = [
            slide?.content?.live_event_text1,
            slide?.content?.live_event_text2,
            slide?.content?.live_event_text3,
          ].filter(Boolean)

          return (
            <div
              key={idx}
              aria-hidden={!active}
              className={`absolute inset-0 max-md:relative ${active ? '' : 'pointer-events-none'}`}
              style={{
                // Mobile: every slide shares one grid cell so they can crossfade in place
                gridArea: '1 / 1',
                zIndex: active ? 1 : 0,
                opacity: active ? 1 : 0,
                transition: `opacity 900ms ${EASE}`,
              }}
            >
              <div
                className="absolute right-0 top-0 h-full w-[66%] overflow-hidden max-md:relative max-md:aspect-[4/3] max-md:h-auto max-md:w-full"
                style={{
                  transform: active ? 'scale(1)' : 'scale(1.06)',
                  transition: `transform 1400ms ${EASE}`,
                }}
              >
                {isEdit || !slide?.player?.type ? (
                  <CustomImage
                    imageUrl={getTemplateImage(slide?.image_s3key)}
                    imgClass="w-full h-full object-cover"
                    loaderClass="w-full h-full object-cover absolute"
                    loaderStyle={{ background: '#5A5A5A50' }}
                    imgAlt={slide?.content?.heading || 'banner'}
                  />
                ) : (
                  <div className="w-full h-full object-cover">
                    <PassimPlayer
                      data={slide?.player}
                      poster={getTemplateImage(slide?.image_s3key)}
                      controlBar={false}
                    />
                  </div>
                )}
              </div>

              {/* Desktop: fade left-to-right into the image */}
              <div
                className="absolute inset-0 z-[1] pointer-events-none max-md:hidden"
                style={{
                  background: `linear-gradient(90deg, ${bgColor} 34%, ${bgColor}EB 44%, ${bgColor}40 62%, ${bgColor}00 80%)`,
                }}
              />
              {/* Mobile: fade the stacked image into the copy */}
              <div
                className="hidden max-md:block absolute inset-0 z-[1] pointer-events-none"
                style={{ background: `linear-gradient(180deg, ${bgColor}00 40%, ${bgColor} 100%)` }}
              />

              <div className="absolute left-[4%] top-1/2 z-[2] w-[min(48%,760px)] -translate-y-1/2 max-md:relative max-md:left-auto max-md:top-auto max-md:-mt-10 max-md:w-auto max-md:translate-y-0 max-md:px-[18px] max-md:pb-[22px]">
                <div
                  style={{
                    opacity: active ? 1 : 0,
                    transform: active ? 'translateY(0)' : 'translateY(20px)',
                    transition: `opacity 700ms ${EASE} ${
                      active ? 250 : 0
                    }ms, transform 900ms ${EASE} ${active ? 250 : 0}ms`,
                  }}
                >
                  {liveTexts.length > 0 && (
                    <div
                      className="mb-4 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[13px] font-bold uppercase tracking-[0.06em] max-md:text-xs"
                      style={{ color: liveColor }}
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{
                          background: liveDotColor,
                          boxShadow: `0 0 0 4px ${liveDotColor}40`,
                        }}
                      />
                      {liveTexts.map((text, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && sep}
                          <span>{text}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  <h1
                    className="mb-3.5 text-[clamp(34px,4vw,72px)] font-extrabold leading-[1.02] tracking-[-0.02em] max-md:text-[30px]"
                    style={{
                      color: getStyleValue('heading', 'color', theme.c7),
                      fontFamily: getStyleValue('heading', 'font', ''),
                    }}
                  >
                    {slide?.content?.heading}
                  </h1>
                  <p
                    className="mb-6 max-w-[46ch] text-base"
                    style={{
                      color: getStyleValue('sub_heading', 'color', theme.c4),
                      fontFamily: getStyleValue('sub_heading', 'font', ''),
                    }}
                  >
                    {slide?.content?.sub_heading}
                  </p>
                  {slide?.cta_button?.enabled && (
                    <button
                      className="inline-flex h-11 items-center justify-center gap-2.5 whitespace-nowrap rounded-md px-[22px] text-[15px] font-bold border-none hover:opacity-90"
                      style={{
                        color: getStyleValue('button', 'color', theme.c7),
                        backgroundColor: getStyleValue('button', 'button_background', '#121418'),
                      }}
                      tabIndex={active ? undefined : -1}
                      onClick={() => handleRedirect(slide?.link)}
                    >
                      ▶&nbsp; {slide?.cta_button?.text}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}

        {slides.length > 1 && (
          <>
            <div className="absolute bottom-7 right-[4%] z-[3] flex gap-2 max-md:hidden">
              <button className={arrowBtn} aria-label="Previous" onClick={() => go(cur - 1)}>
                ‹
              </button>
              <button className={arrowBtn} aria-label="Next" onClick={() => go(cur + 1)}>
                ›
              </button>
            </div>
            <div className="absolute inset-x-0 bottom-6 z-[3] flex justify-center gap-2 max-md:static max-md:pb-1 max-md:pt-3">
              {slides.map((s, i) => (
                <button
                  key={i}
                  aria-label={`Go to slide ${i + 1}: ${s?.content?.heading || ''}`}
                  aria-current={i === cur || undefined}
                  className="w-[9px] h-[9px] rounded-full border-none p-0"
                  style={{
                    background:
                      i === cur
                        ? getStyleValue('component_background', 'dot_color_active', '#E4022B')
                        : getStyleValue('component_background', 'dot_color_inactive', '#5C5D63'),
                  }}
                  onClick={() => go(i)}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {isEdit && hoverState.length > 0 && <AddSection openAdd={handleAddModal} />}
    </div>
  )
}

export default withTheme(AthleteOwnedSportsBanner)
